import Add from "@mui/icons-material/Add";
import Adjust from "@mui/icons-material/Adjust";
import Explore from "@mui/icons-material/Explore";
import Remove from "@mui/icons-material/Remove";
import { Stack, Tooltip } from "@mui/material";
import { usePagePrefs } from "@pepsico/pref-permissions";
import { useMap } from "react-map-gl/mapbox";

import { useGetGrowersGeoByLocation } from "api/growers-geo/growers-geo";
import type { GrowerGeoResponse } from "api/model";
import {
  fitMapToLocationView,
  getBoundariesBounds,
} from "components/PlotMap/utils/mapCameraUtils";

import LayersControl from "./LayersControl";
import MapControlIconButton from "./MapControlIconButton";

interface MapViewControlsProps {}

const parseCoordPref = (
  value: string | string[] | undefined,
): number | null => {
  if (typeof value !== "string" || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const MapViewControls: React.FC<MapViewControlsProps> = () => {
  const { default: map } = useMap();
  const [prefs, isReady] = usePagePrefs();

  const lat = parseCoordPref(prefs.lat);
  const lng = parseCoordPref(prefs.lng);
  const hasLatLng = lat != null && lng != null;
  const locationUuid = (prefs.location as string | null | undefined) ?? "";

  const { data: growersGeo = [] } = useGetGrowersGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): GrowerGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );

  const isMapReady = map != null && isReady;

  const handleZoomIn = () => {
    map?.zoomIn();
  };

  const handleZoomOut = () => {
    map?.zoomOut();
  };

  const handleZoomToLocation = () => {
    fitMapToLocationView({
      boundaryBounds: getBoundariesBounds(
        growersGeo.map((grower) => grower.boundary),
      ),
      hasLatLng,
      lat,
      lng,
      map,
    });
  };

  const handleNorthUp = () => {
    map?.resetNorth({ duration: 300 });
  };

  return (
    <Stack aria-label="Map view controls" role="group" spacing={0.5}>
      <LayersControl />
      <Tooltip title="Zoom in">
        <span>
          <MapControlIconButton
            aria-label="Zoom in"
            disabled={!isMapReady}
            onClick={handleZoomIn}
          >
            <Add />
          </MapControlIconButton>
        </span>
      </Tooltip>
      <Tooltip title="Zoom out">
        <span>
          <MapControlIconButton
            aria-label="Zoom out"
            disabled={!isMapReady}
            onClick={handleZoomOut}
          >
            <Remove />
          </MapControlIconButton>
        </span>
      </Tooltip>
      <Tooltip title="Zoom to location">
        <span>
          <MapControlIconButton
            aria-label="Zoom to location"
            disabled={!isMapReady}
            onClick={handleZoomToLocation}
          >
            <Adjust />
          </MapControlIconButton>
        </span>
      </Tooltip>
      <Tooltip title="North up">
        <span>
          <MapControlIconButton
            aria-label="North up"
            disabled={!isMapReady}
            onClick={handleNorthUp}
          >
            <Explore />
          </MapControlIconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};

export default MapViewControls;
